// Vortex Power Solutions — Product Catalog
import { IMGS } from "@/lib/images";

const IMG = {
  // Unique images for the mega menu
  servoSingle: "/imgs/servo-single-phase.png",
  servoThree: "/imgs/servo-three-phase.png",
  servoAir: "/imgs/servo-air-cooled.png",
  servoOil: "/imgs/servo-oil-cooled.png",

  isolationSingle: "/imgs/isolation-single-phase.png",
  isolationThree: "/imgs/isolation-three-phase.png",
  isolationAir: "/imgs/isolation-air-cooled.png",
  isolationOil: "/imgs/isolation-oil-cooled.png",

  apsc: "/imgs/apsc.png",
  apc: "/imgs/apc.png",
  timer: "/imgs/timers.png",
  stabilizer: "/imgs/stabilizer.png",
  timersall: "/imgs/timersall.png",

};

// Compact mega-menu tiles: 4×3 grid (12 items) — variants + parents
// Mega menu: 12 products with separate image paths
export const MEGA_MENU_ITEMS = [
  {
    name: "Single Phase Servo",
    slug: "servo-voltage-stabilizer",
    image: IMG.servoSingle,
  },
  {
    name: "Three Phase Servo",
    slug: "servo-voltage-stabilizer",
    image: IMG.servoThree,
  },
  {
    name: "Air Cooled Servo",
    slug: "servo-voltage-stabilizer",
    image: IMG.servoAir,
  },
  {
    name: "Oil Cooled Servo",
    slug: "servo-voltage-stabilizer",
    image: IMG.servoOil,
  },
  {
    name: "Single Phase Isolation Transformer",
    slug: "isolation-transformer",
    image: IMG.isolationSingle,
  },
  {
    name: "Three Phase Isolation Transformer",
    slug: "isolation-transformer",
    image: IMG.isolationThree,
  },
  {
    name: "Air Cooled Isolation Transformer",
    slug: "isolation-transformer",
    image: IMG.isolationAir,
  },
  {
    name: "Oil Cooled Isolation Transformer",
    slug: "isolation-transformer",
    image: IMG.isolationOil,
  },
  {
    name: "Phase Sequence Corrector",
    slug: "phase-sequence-corrector",
    image: IMG.apsc,
  },
  {
    name: "Automatic Phase Changer",
    slug: "phase-changer",
    image: IMG.apc,
  },
  {
    name: "Multi-Purpose Timers",
    slug: "multi-purpose-timers",
    image: IMG.timer,
  },
  {
    name: "Voltage Stabilizers",
    slug: "voltage-stabilizer",
    image: IMG.stabilizer,
  },
];

export const CATEGORIES = [
  { slug: "servo-stabilizer", label: "Servo Stabilizers" },
  { slug: "isolation-transformer", label: "Isolation Transformers" },
  { slug: "phase-sequence-corrector", label: "Automatic Phase Sequence Corrector" },
  { slug: "phase-changer", label: "Automatic Phase Changer" },
  { slug: "timersall", label: "Multi-Purpose Timers" },
  { slug: "voltage-stabilizer", label: "Voltage Stabilizers" },
];

export const PRODUCTS = [
  {
    slug: "servo-voltage-stabilizer",
    category: "servo-stabilizer",
    name: "Servo Voltage Stabilizers",
    short:
      "Precision servo-controlled voltage regulation for critical industrial loads — single or three phase, air or oil cooled.",
    image: IMG.servoThree,
    overview:
      "Vortex Servo Voltage Stabilizers deliver precision ±1% output regulation across a wide input range. Engineered with a torque-motor-driven variac and a solid-state control card, they protect sensitive industrial and medical loads from voltage sags, surges and prolonged fluctuations.",
    variants: ["Single Phase", "Three Phase", "Air Cooled", "Oil Cooled"],
    applications: [
      "CNC and VMC machines",
      "Hospitals & Diagnostic Centers",
      "Textile & Printing Machines",
      "IT Parks & Data Centers",
      "Manufacturing Plants",
      "Commercial Buildings",
    ],
    features: [
      "±1% Output regulation accuracy",
      "Wide input range 140V–280V (single phase)",
      "Copper wound isolation core option",
      "Digital LED / LCD metering",
      "Overload / short circuit protection",
      "Auto bypass switch",
      "Low no-load losses",
    ],
    advantages: [
      "Extended equipment lifespan",
      "Lower energy bills",
      "Faster ROI vs UPS-only setups",
      "Minimal maintenance",
    ],
    specs: [
      { label: "Rating", value: "3 kVA – 3000 kVA" },
      { label: "Input Voltage", value: "140V – 280V (1Ø) / 300V – 460V (3Ø)" },
      { label: "Output Voltage", value: "230V ±1% / 400V ±1%" },
      { label: "Frequency", value: "47 – 53 Hz" },
      { label: "Correction Speed", value: "40 V/sec" },
      { label: "Cooling", value: "Natural Air / Forced Air / Oil Cooled" },
      { label: "Duty Cycle", value: "100% Continuous" },
      { label: "Efficiency", value: "> 97%" },
    ],
    faq: [
      {
        q: "What is the difference between air-cooled and oil-cooled servo stabilizers?",
        a: "Air-cooled units use natural or forced air convection and suit lower loads and cleaner environments, while oil-cooled units use transformer oil for heat dissipation and handle higher continuous ratings with better overload capacity.",
      },
      {
        q: "Do servo stabilizers protect against spikes?",
        a: "They regulate slow to medium voltage variations. For high-frequency spikes and surges, we recommend pairing with our isolation transformer or a surge protection device.",
      },
      {
        q: "Can they be customized for specific input ranges?",
        a: "Yes. We build custom variants down to 90V or up to 500V input on request.",
      },
    ],
  },
  {
    slug: "isolation-transformer",
    category: "isolation-transformer",
    name: "Isolation Transformers",
    short:
      "Ultra-isolated K-rated transformers that eliminate common-mode noise and protect sensitive loads.",
    image: IMG.isolation,
    overview:
      "Vortex Isolation Transformers provide galvanic separation between the source and the load, blocking common-mode noise, transients and harmonic distortion. Ideal for medical, semiconductor and precision manufacturing environments.",
    variants: ["Single Phase", "Three Phase", "Air Cooled", "Oil Cooled"],
    applications: [
      "Medical equipment (MRI, CT, X-Ray)",
      "Semiconductor fabrication",
      "R&D and testing labs",
      "Precision CNC machining",
      "Data centers",
    ],
    features: [
      "Ultra-isolated dual-shielded design",
      "Attenuation > 100 dB",
      "K-13 / K-20 rated for non-linear loads",
      "Copper winding, CRGO core",
      "Electrostatic shield between windings",
      "Low leakage current (< 100 µA)",
    ],
    advantages: [
      "Eliminates ground loops",
      "Neutral–earth voltage down to < 1V",
      "Protects sensitive electronics",
      "Reduces harmonic distortion",
    ],
    specs: [
      { label: "Rating", value: "1 kVA – 2000 kVA" },
      { label: "Isolation", value: "Dual electrostatic shield" },
      { label: "Attenuation", value: "> 100 dB @ 1 MHz" },
      { label: "Regulation", value: "< 3%" },
      { label: "Efficiency", value: "> 98%" },
      { label: "Cooling", value: "AN / ANAF / ONAN" },
      { label: "Insulation Class", value: "Class B / F / H" },
    ],
    faq: [
      {
        q: "Do I need a stabilizer with an isolation transformer?",
        a: "Isolation transformers do not regulate voltage. For fluctuating supplies, pair the isolation transformer with a servo stabilizer.",
      },
      {
        q: "What is a K-rated transformer?",
        a: "K-rated transformers are designed to handle harmonic currents from non-linear loads without overheating. We offer K-4, K-13 and K-20 variants.",
      },
    ],
  },
  {
    slug: "phase-sequence-corrector",
    category: "phase-sequence-corrector",
    name: "Automatic Phase Sequence Corrector",
    short:
      "Automatically restores correct phase sequence to protect three-phase motors and pumps from reverse rotation.",
    image: IMG.apsc,
    overview:
      "The Vortex APSC continuously monitors the incoming three-phase supply and automatically corrects any phase reversal condition — guarding motors, compressors, chillers and pumps against costly reverse-rotation damage.",
    variants: ["Single Phase 63A", "63A – 400A", "With Digital Display"],
    applications: [
      "HVAC & Chiller Plants",
      "Water Pumping Stations",
      "Elevators & Escalators",
      "Compressor Systems",
      "Textile & CNC Machinery",
    ],
    features: [
      "Fully automatic phase correction",
      "Phase failure / unbalance protection",
      "Under / over voltage cut-off",
      "Digital voltmeter & phase indicator",
      "Auto-reset with adjustable delay",
      "MCB-protected input",
    ],
    advantages: [
      "No manual re-wiring required",
      "Prevents motor burnout",
      "Extends equipment life",
      "24×7 continuous protection",
    ],
    specs: [
      { label: "Rating", value: "63A / 100A / 200A / 400A" },
      { label: "Nominal Voltage", value: "415V ±20%" },
      { label: "Correction Time", value: "< 100 ms" },
      { label: "Enclosure", value: "Powder coated / SS304 optional" },
      { label: "Ambient", value: "0 – 50 °C" },
      { label: "IP Rating", value: "IP54" },
    ],
    faq: [
      {
        q: "Does APSC also protect against single-phasing?",
        a: "Yes, all Vortex APSC models include single-phasing preventer and unbalance protection.",
      },
    ],
  },
  {
    slug: "phase-changer",
    category: "phase-changer",
    name: "Automatic Phase Changer",
    short:
      "Detects and switches to a healthy phase automatically — ensures uninterrupted single-phase output from a three-phase source.",
    image: IMG.apc,
    overview:
      "Vortex Automatic Phase Changer monitors R-Y-B phases and automatically selects the healthy phase to feed single-phase loads. It ensures continuous supply to critical loads even when one or two incoming phases fail.",
    variants: ["16A", "32A", "63A", "With Voltage Cut-Off"],
    applications: [
      "Residential & Commercial buildings",
      "Petrol pumps & Retail outlets",
      "ATMs & Telecom shelters",
      "Small clinics & Labs",
      "Home offices",
    ],
    features: [
      "Automatic phase selection (< 500 ms)",
      "High / low voltage cut-off",
      "Digital voltmeter",
      "Auto restart on healthy phase",
      "LED indication for each phase",
    ],
    advantages: [
      "Eliminates manual switching",
      "Zero downtime for critical loads",
      "Compact wall-mount design",
    ],
    specs: [
      { label: "Rating", value: "16A / 32A / 63A" },
      { label: "Input", value: "3 Phase, 415V" },
      { label: "Output", value: "1 Phase, 230V" },
      { label: "Cut-off Range", value: "170V – 270V (adjustable)" },
      { label: "Enclosure", value: "ABS / Metal, IP20" },
    ],
    faq: [
      {
        q: "Can APC be used with a generator supply?",
        a: "Yes, APC works with both DG and mains supply as long as neutral is common.",
      },
    ],
  },
  {
    slug: "multi-purpose-timers",
    category: "timers",
    name: "Multi-Purpose Timers",
    short:
      "Reliable industrial timers — cyclic, ATM, water-level, clock and AC-fan wireless control.",
    image: IMG.timersall,
    overview:
      "A complete range of programmable timers for industrial automation, ATM cabin cooling, water level control, cyclic operations and wireless AC fan scheduling.",
    variants: [
      "ATM Timer",
      "Cyclic Timer",
      "Water Level Controller",
      "AC Fan Wireless Timer",
      "Clock Timer",
    ],
    applications: [
      "ATM cabins",
      "Water pumping stations",
      "HVAC / AC scheduling",
      "Cyclic dosing pumps",
      "Street lighting automation",
    ],
    features: [
      "Digital 7-segment display",
      "Battery backup memory",
      "16 programmable events (Clock timer)",
      "Wireless RF pairing (AC fan timer)",
      "IP54 sealed enclosure",
    ],
    advantages: [
      "Energy savings 20–40%",
      "Maintenance-free operation",
      "Wide 90V – 300V input",
    ],
    specs: [
      { label: "Supply", value: "230V AC ±20%" },
      { label: "Contact", value: "SPDT 16A" },
      { label: "Accuracy", value: "±1 sec / day" },
      { label: "Backup", value: "72 hours (typ.)" },
      { label: "Mounting", value: "Wall / Panel / DIN" },
    ],
    faq: [
      {
        q: "Is the water level controller compatible with borewell pumps?",
        a: "Yes, it supports both single-phase and three-phase pumps up to 3 HP directly and higher with external contactors.",
      },
    ],
  },
  {
    slug: "voltage-stabilizer",
    category: "voltage-stabilizer",
    name: "Voltage Stabilizers",
    short:
      "Wall-mount digital & automatic stabilizers for ACs, refrigerators, home theatres and light industrial loads.",
    image: IMG.stabilizer,
    overview:
      "Vortex Voltage Stabilizers use step-relay technology with a microcontroller-based control card. Available as digital and automatic variants for HVAC, home and light-industrial protection.",
    variants: ["Digital Stabilizer", "Automatic Stabilizer"],
    applications: [
      "Split & Cassette ACs",
      "Refrigerators & Deep Freezers",
      "Home Theatres & TVs",
      "Small Commercial Loads",
      "Home Offices",
    ],
    features: [
      "Digital 3-digit voltage display",
      "Time-delayed switching",
      "Overload & short-circuit protection",
      "High / low voltage cut-off",
      "Wall-mount metal enclosure",
    ],
    advantages: [
      "Extends appliance life",
      "Silent operation",
      "Compact & lightweight",
    ],
    specs: [
      { label: "Rating", value: "1 kVA – 15 kVA" },
      { label: "Input Range", value: "130V – 280V" },
      { label: "Output", value: "230V ±10%" },
      { label: "Correction Speed", value: "< 30 ms" },
      { label: "Enclosure", value: "CRCA Steel, Powder Coated" },
    ],
    faq: [
      {
        q: "Which stabilizer is best for a 1.5 ton AC?",
        a: "A 4 kVA digital stabilizer with 130–280V input range is our recommended choice for a 1.5-ton split AC.",
      },
    ],
  },
];

export const PRODUCT_MAP = Object.fromEntries(PRODUCTS.map((p) => [p.slug, p]));

export const INDUSTRIES = [
  { name: "Healthcare & Hospitals", icon: "HeartPulse" },
  { name: "Manufacturing", icon: "Factory" },
  { name: "Textile & Printing", icon: "Shirt" },
  { name: "Engineering", icon: "Cog" },
  { name: "IT Parks", icon: "Building2" },
  { name: "Commercial Buildings", icon: "Building" },
  { name: "Education", icon: "GraduationCap" },
  { name: "Data Centers", icon: "ServerCog" },
  { name: "Pharmaceuticals", icon: "FlaskConical" },
  { name: "Food & Beverage", icon: "UtensilsCrossed" },
];

export const REVIEWS = [
  {
    name: "Rajesh Kumar",
    role: "Plant Manager, Chennai Textiles",
    rating: 5,
    text:
      "We installed 5 servo stabilizers from Vortex across our weaving line. Zero downtime in 18 months. Their service team responds within hours.",
  },
  {
    name: "Dr. Priya Venkatesh",
    role: "Director, Meridian Diagnostics",
    rating: 5,
    text:
      "The isolation transformer for our MRI machine has been flawless. Vortex delivered exactly as promised, on time, and their commissioning team was excellent.",
  },
  {
    name: "Arun Suresh",
    role: "Facility Head, TechPark IT Solutions",
    rating: 5,
    text:
      "Migrated all our building panels to Vortex APSC + servo units. Power quality issues reduced by 90%. Highly recommend for critical facilities.",
  },
  {
    name: "Meena Iyer",
    role: "Owner, Sri Krishna Printers",
    rating: 5,
    text:
      "Best after-sales service in Chennai. The team walked me through spec selection and even helped optimize our load calculation. Truly a partner, not just a vendor.",
  },
];
